import React from "react";

import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { useToast } from "@racoon/ui";
import { Upload } from "lucide-react";

import { PhotoReportCarousel, type ReportPhoto } from "./photo-report.carousel";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useGetJobByIdQuery, useUploadJobFileMutation } from "~/shared/api/jobs/job.grpc";
import {
  INSTRUCTION_PHOTO_ACCEPT,
  MAX_INSTRUCTION_PHOTO_BYTES,
  encodePhotoFile,
} from "~/shared/lib/instruction-photos";

interface PhotoReportTabProps {
  job: ConnectProto.Job;
  /** A failure photo's label: the job's incident, else «После». */
  failureLabel?: string;
}

interface UploadJobPhotoButtonProps {
  job: ConnectProto.Job;
  type: ConnectProto.FileUploadType;
}

const UploadJobPhotoButton: React.FC<UploadJobPhotoButtonProps> = ({ job, type }) => {
  const t = useT();
  const { toast } = useToast();
  const [uploadJobFile, { isLoading: isUploading }] = useUploadJobFileMutation();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handlePhotoPicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (!file) return;

    if (!INSTRUCTION_PHOTO_ACCEPT.split(",").includes(file.type)) {
      toast({ title: t(Namespace.Orders, "create.photoErrorType"), variant: "destructive" });
      return;
    }
    if (file.size > MAX_INSTRUCTION_PHOTO_BYTES) {
      toast({ title: t(Namespace.Orders, "create.photoErrorSize"), variant: "destructive" });
      return;
    }

    let encoded: Awaited<ReturnType<typeof encodePhotoFile>>;
    try {
      encoded = await encodePhotoFile(file);
    } catch {
      toast({ title: t(Namespace.Errors, "somethingWentWrong"), variant: "destructive" });
      return;
    }

    try {
      await uploadJobFile({
        // connectBaseQuery normalizes response bigints to strings, so the id
        // arrives as a string despite the proto type.
        jobId: BigInt(job.id),
        type,
        containerName: job.containerName,
        file: encoded,
      }).unwrap();
      toast({ title: t(Namespace.PhotoReport, "photoUploaded") });
    } catch {
      // Already toasted by the global error middleware.
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={INSTRUCTION_PHOTO_ACCEPT}
        className="hidden"
        onChange={handlePhotoPicked}
      />
      <button
        type="button"
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
        aria-label={t(Namespace.PhotoReport, "uploadPhoto")}
        title={t(Namespace.PhotoReport, "uploadPhoto")}
        className="flex items-center justify-center size-8 rounded-lg bg-surface-invert text-white hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Upload className="size-4" />
      </button>
    </>
  );
};

const { JOB_SERVICE_STARTED, JOB_SERVICE_ENDED, ROUTE_TASK_FAILURE_REASON } =
  ConnectProto.FileUploadType;

export const PhotoReportTab: React.FC<PhotoReportTabProps> = ({ job, failureLabel }) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();

  // The modal renders a snapshot of the job taken when it opened, so an upload's
  // cache invalidation never reaches it. Read the photos from a live query instead:
  // it refetches on the upload's "Order" invalidation and the new photo shows here.
  const { data: liveJob } = useGetJobByIdQuery(
    {
      id: BigInt(job.id),
      serviceReceiverId: BigInt(job.serviceReceiverId ?? 0),
      serviceProviderId: BigInt(job.serviceProviderId ?? 0),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !job.id || !job.serviceReceiverId || !job.serviceProviderId,
    },
  );
  const photoSource = liveJob ?? job;

  const fileReports = photoSource.fileReports ?? [];
  const { serviceStartedReportUrl, serviceEndedReportUrl } = photoSource;
  const { containerName } = job;

  // The deprecated report URLs stand in as photos of their own kind
  const legacy = (url: string | undefined, type: ConnectProto.FileUploadType) =>
    url
      ? [
          {
            id: 0n,
            ownerOrganizationId: BigInt(selectedCompanyId!),
            createdAt: job.factEndAt,
            type,
            url,
            containerName,
          } as ConnectProto.FileUpload,
        ]
      : [];
  const ofType = (type: ConnectProto.FileUploadType) =>
    fileReports.filter((item) => item.type === type);
  const label = (photos: ConnectProto.FileUpload[], text: string, containerAction: string) =>
    photos.map((photo): ReportPhoto => ({ photo, label: text, containerAction }));
  const after = t(Namespace.Common, "after");
  const placed = t(Namespace.PhotoReport, "placed");
  // A Невывоз opens on its evidence
  const items = [
    ...label(ofType(ROUTE_TASK_FAILURE_REASON), failureLabel ?? after, placed),
    ...label(
      [...ofType(JOB_SERVICE_STARTED), ...legacy(serviceStartedReportUrl, JOB_SERVICE_STARTED)],
      t(Namespace.Common, "before"),
      t(Namespace.PhotoReport, "pickedUp"),
    ),
    ...label(
      [...ofType(JOB_SERVICE_ENDED), ...legacy(serviceEndedReportUrl, JOB_SERVICE_ENDED)],
      after,
      placed,
    ),
  ];

  const hasBefore = items.some(({ photo }) => photo.type === JOB_SERVICE_STARTED);
  const hasAfter = items.some(({ photo }) => photo.type !== JOB_SERVICE_STARTED);

  // Nothing yet: a slot per kind, each with its upload
  if (items.length === 0) {
    return (
      <div className="grid grid-cols-2 gap-4">
        {[JOB_SERVICE_STARTED, JOB_SERVICE_ENDED].map((type) => (
          <div
            key={type}
            className="relative flex h-full min-h-[280px] w-full items-center justify-center rounded-lg bg-gray-200"
          >
            <div className="absolute bottom-2 right-2">
              <UploadJobPhotoButton job={job} type={type} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // The kind still missing takes its upload in the carousel
  const missing = !hasBefore ? JOB_SERVICE_STARTED : !hasAfter ? JOB_SERVICE_ENDED : undefined;
  return (
    <PhotoReportCarousel
      items={items}
      overlayAction={missing && <UploadJobPhotoButton job={job} type={missing} />}
    />
  );
};
